import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { Close } from "@mui/icons-material";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  IconButtonProps,
  Stack,
  SxProps,
  Theme,
} from "@mui/material";
import AccordionIcon from "assets/icons/AccordionIcon";
import { getSearchParamsWithIdsParam } from "helpers/index";
import qs from "qs";
import { ComponentType, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

type WithAccordionProps<T> = T & {
  sx?: SxProps<Theme>;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  handleIconNode?: ReactNode;
  withDragHandleIcon?: boolean;
  entityId?: string;
  idsSearchParamName?: string;
  onRemove?: () => void;
  removeButtonProps?: IconButtonProps;
  initExpanded?: boolean;
  accordionSummaryTitle?: ReactNode;
};

const withAccordion = <T,>(Component: ComponentType<T>) => {
  // eslint-disable-next-line react/function-component-definition, react/display-name
  return (props: WithAccordionProps<T>) => {
    const {
      sx,
      dragHandleProps,
      handleIconNode,
      withDragHandleIcon = false,
      entityId,
      idsSearchParamName,
      onRemove,
      removeButtonProps,
      initExpanded = false,
      accordionSummaryTitle,
    } = props;
    const ref = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(initExpanded);
    const [entered, setEntered] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]) as {
      scrollIntoViewId?: string;
    } & Record<string, string>;
    const { scrollIntoViewId } = params;
    const searchParamIds = idsSearchParamName ? params[idsSearchParamName] : undefined;
    const searchParamIdsArr = searchParamIds?.split(",") || [];

    const handleRemove = onRemove
      ? (e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onRemove();
        }
      : undefined;

    const setIdsSearchParam = (id: string, paramName: string) => {
      const resultParams = getSearchParamsWithIdsParam(id, paramName, params);
      setSearchParams(qs.stringify(resultParams), { replace: true });
    };

    const toggleExpanded = () => {
      if (idsSearchParamName && entityId) {
        setIdsSearchParam(entityId, idsSearchParamName);
      } else {
        setExpanded(!expanded);
      }
    };

    useEffect(() => {
      if (idsSearchParamName && entityId) {
        const searchParamIdsEntityIndex = searchParamIdsArr?.findIndex((paramId) => paramId === entityId);

        if (searchParamIdsEntityIndex === -1) {
          if (expanded) setExpanded(false);
        } else if (!expanded) setExpanded(true);
      }
    }, [searchParamIdsArr, idsSearchParamName, entityId]);

    useEffect(() => {
      if (ref && scrollIntoViewId && scrollIntoViewId === entityId && entered) {
        ref.current?.scrollIntoView({ behavior: "smooth" });
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { scrollIntoViewId: _, ...otherParams } = params;
        setSearchParams(qs.stringify(otherParams), { replace: true });
      }
    }, [scrollIntoViewId, ref, entered]);

    return (
      <Accordion
        ref={ref}
        expanded={expanded}
        onChange={toggleExpanded}
        slotProps={{
          transition: {
            onEntered: () => setEntered(true),
            onExited: () => setEntered(false),
          },
        }}
        sx={{
          borderRadius: 2,
          "& .MuiAccordionSummary-content.Mui-expanded": { marginBottom: 0 },
          "& .MuiAccordionSummary-content": { maxWidth: "100%" },
          ...sx,
        }}>
        <div {...dragHandleProps}>
          <AccordionSummary sx={{ "&.Mui-focusVisible": { backgroundColor: "transparent" } }}>
            <Stack
              direction='row'
              spacing={2}
              sx={{ alignItems: "center", width: "100%" }}>
              {withDragHandleIcon && dragHandleProps && handleIconNode}
              {accordionSummaryTitle}
            </Stack>
            <AccordionActions sx={{ marginLeft: "auto", padding: 0 }}>
              <IconButton
                sx={{
                  color: "base.200",
                  transition: "transform 300ms ease",
                  transform: expanded ? "rotateZ(180deg)" : "none",
                }}>
                <AccordionIcon />
              </IconButton>
              {handleRemove && (
                <IconButton
                  onClick={handleRemove}
                  sx={{ color: "base.200" }}
                  {...removeButtonProps}>
                  <Close />
                </IconButton>
              )}
            </AccordionActions>
          </AccordionSummary>
        </div>
        <AccordionDetails>
          <Component {...props} />
        </AccordionDetails>
      </Accordion>
    );
  };
};

export default withAccordion;
